import { useMemo } from "react";
import type {
  BatchJob,
  DocumentSectionsRefreshItemResult,
  DocumentSectionsRefreshSummary,
} from "../../../../../lib/corpusApi";
import { narrowByOutcome, type BatchJobItemRow } from "../../panel/batch_job_item_rows";
import {
  useBatchJobItemList,
  type UseBatchJobItemListResult,
} from "../../panel/useBatchJobItemList";
import { useBatchJobTabs, type UseBatchJobTabsResult } from "../../panel/useBatchJobTabs";
import {
  DOCUMENT_SECTIONS_REFRESH_TABS,
  type DocumentSectionsRefreshTabConfig,
  type DocumentSectionsRefreshTabKey,
} from "./document_sections_refresh_tabs";

/** One list row: the raw item plus its document_sections_refresh result. A failed item has none. */
export type DocumentSectionsRefreshRow = BatchJobItemRow<DocumentSectionsRefreshItemResult>;

/** What the panel header shows, read from the job's stored summary. */
export interface DocumentSectionsRefreshPanelSummary {
  refreshedCount: number;
  skippedCount: number;
  paragraphsEmbedded: number;
}

export interface UseDocumentSectionsRefreshPanelResult {
  /** Null while the job has not finished, as its summary is only written at the end. */
  summary: DocumentSectionsRefreshPanelSummary | null;
  tabs: UseBatchJobTabsResult<DocumentSectionsRefreshTabKey, DocumentSectionsRefreshTabConfig>;
  list: UseBatchJobItemListResult<DocumentSectionsRefreshItemResult>;
}

const narrowDocumentSectionsRefreshResult = narrowByOutcome<DocumentSectionsRefreshItemResult>(
  new Set(["refreshed", "skipped"])
);

/** Reads a job's stored summary into what the panel header shows; null when there is none yet. */
export function toPanelSummary(
  stored: Record<string, unknown> | null
): DocumentSectionsRefreshPanelSummary | null {
  if (!stored) return null;
  const summary = stored as unknown as DocumentSectionsRefreshSummary;
  return {
    refreshedCount: summary.byOutcome.refreshed ?? 0,
    skippedCount: summary.byOutcome.skipped ?? 0,
    paragraphsEmbedded: summary.paragraphsEmbedded,
  };
}

/** Drives the document_sections_refresh batch job panel: the job's summary, and one paginated list per tab.
 *  Read-only — a refresh has no review. */
export function useDocumentSectionsRefreshPanel(
  job: BatchJob
): UseDocumentSectionsRefreshPanelResult {
  const tabs = useBatchJobTabs(DOCUMENT_SECTIONS_REFRESH_TABS, "refreshed");
  const list = useBatchJobItemList({
    job,
    filters: tabs.tab.apiFilters,
    resetKey: tabs.activeTab,
    narrow: narrowDocumentSectionsRefreshResult,
  });
  const summary = useMemo(() => toPanelSummary(job.summary), [job.summary]);

  return { summary, tabs, list };
}
